/**
 * Email API Client
 * Epic 5: Bulk Email (ADR-090)
 */

import { api } from './client';

// ============================================
// TYPES
// ============================================

export interface BulkSendRequest {
  partnerIds: string[];
  templateSlug: string;
  language?: string | undefined;
  variables?: Record<string, string> | undefined;
}

export interface BulkSendSkipped {
  partnerId: string;
  reason: 'no_email' | 'not_found';
}

export interface BulkSendError {
  partnerId: string;
  error: string;
}

export interface BulkSendResult {
  queued: number;
  skipped: number;
  failed: number;
  skippedDetails: BulkSendSkipped[];
  errors: BulkSendError[];
}

export interface EmailLogEntry {
  id: string;
  templateSlug: string;
  recipientEmail: string;
  subject: string;
  status: 'queued' | 'sent' | 'failed' | 'rejected';
  errorMessage?: string | null;
  jobId?: string | null;
  retryCount: number;
  sentAt?: string | null;
  createdAt: string;
}

export interface EmailLogStats {
  sent: number;
  failed: number;
  rejected: number;
  queued: number;
}

export interface EmailLogQueryResult {
  items: EmailLogEntry[];
  total: number;
  page: number;
  limit: number;
  stats: EmailLogStats;
}

// ============================================
// API CALLS
// ============================================

export async function bulkSendEmail(request: BulkSendRequest): Promise<BulkSendResult> {
  const response = await api.post<{ data: BulkSendResult }>('/email/bulk-send', request);
  return response.data;
}

export async function getEmailLogs(params?: {
  status?: string;
  templateSlug?: string;
  from?: string;
  to?: string;
  page?: number;
  limit?: number;
}): Promise<EmailLogQueryResult> {
  const searchParams = new URLSearchParams();
  if (params?.status) searchParams.set('status', params.status);
  if (params?.templateSlug) searchParams.set('templateSlug', params.templateSlug);
  if (params?.from) searchParams.set('from', params.from);
  if (params?.to) searchParams.set('to', params.to);
  if (params?.page) searchParams.set('page', String(params.page));
  if (params?.limit) searchParams.set('limit', String(params.limit));

  const query = searchParams.toString();
  const response = await api.get<{ data: EmailLogQueryResult }>(
    `/settings/email-logs${query ? `?${query}` : ''}`
  );
  return response.data;
}

export async function resendEmail(logId: string): Promise<{ success: boolean; message: string }> {
  const response = await api.post<{ data: { success: boolean; message: string } }>(
    `/settings/email-logs/${logId}/resend`
  );
  return response.data;
}
